import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  nursingOPDAssessments: [],
  currentNursingOPDAssessment: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all nursing OPD assessments
export const fetchNursingOPDAssessments = createAsyncThunk(
  'nursingOPDAssessments/fetchNursingOPDAssessments',
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/nursing-opd-assessments?patient_id=${patientId}`
        : '/nursing-opd-assessments';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Nursing OPD assessments'
      );
    }
  }
);

// Fetch single nursing OPD assessment
export const fetchNursingOPDAssessment = createAsyncThunk(
  'nursingOPDAssessments/fetchNursingOPDAssessment',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/nursing-opd-assessments/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Nursing OPD assessment'
      );
    }
  }
);

// Create new nursing OPD assessment
export const createNursingOPDAssessment = createAsyncThunk(
  'nursingOPDAssessments/createNursingOPDAssessment',
  async (assessmentData, { rejectWithValue }) => {
    try {
      const response = await api.post(`/nursing-opd-assessments`, assessmentData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) return rejectWithValue(error.response.data.errors);
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create Nursing OPD assessment'
      );
    }
  }
);

// Update nursing OPD assessment
export const updateNursingOPDAssessment = createAsyncThunk(
  'nursingOPDAssessments/updateNursingOPDAssessment',
  async ({ id, assessmentData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/nursing-opd-assessments/${id}`, assessmentData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) return rejectWithValue(error.response.data.errors);
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update Nursing OPD assessment'
      );
    }
  }
);

// Delete nursing OPD assessment
export const deleteNursingOPDAssessment = createAsyncThunk(
  'nursingOPDAssessments/deleteNursingOPDAssessment',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/nursing-opd-assessments/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete Nursing OPD assessment'
      );
    }
  }
);

// ==================== Slice ====================
const nursingOPDAssessmentsSlice = createSlice({
  name: 'nursingOPDAssessments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentNursingOPDAssessment: (state, action) => {
      state.currentNursingOPDAssessment = action.payload;
    },
    clearCurrentNursingOPDAssessment: (state) => {
      state.currentNursingOPDAssessment = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchNursingOPDAssessments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNursingOPDAssessments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.nursingOPDAssessments = action.payload;
      })
      .addCase(fetchNursingOPDAssessments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchNursingOPDAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNursingOPDAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentNursingOPDAssessment = action.payload;
      })
      .addCase(fetchNursingOPDAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createNursingOPDAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createNursingOPDAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.nursingOPDAssessments.unshift(action.payload);
      })
      .addCase(createNursingOPDAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateNursingOPDAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateNursingOPDAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;

        const index = state.nursingOPDAssessments.findIndex((d) => d.id === updated.id);
        if (index !== -1) state.nursingOPDAssessments[index] = updated;

        if (state.currentNursingOPDAssessment?.id === updated.id) {
          state.currentNursingOPDAssessment = updated;
        }
      })
      .addCase(updateNursingOPDAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteNursingOPDAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteNursingOPDAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.nursingOPDAssessments = state.nursingOPDAssessments.filter(
          (d) => d.id !== action.payload
        );

        if (state.currentNursingOPDAssessment?.id === action.payload) {
          state.currentNursingOPDAssessment = null;
        }
      })
      .addCase(deleteNursingOPDAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCurrentNursingOPDAssessment, clearCurrentNursingOPDAssessment } =
  nursingOPDAssessmentsSlice.actions;
export default nursingOPDAssessmentsSlice.reducer;
